
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

import TmcAdmissionsFilters from '@/modules/TmcModule/components/TmcAdmissionsFilters/index.vue'

// constants
import { TmcAdmissionsColumns } from '@/core/constants/columns/TmcAdmissions.columns'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { AdmissionT, TmcGetAdmissionsListParams } from '@/core/types/tmc.types'

// realisations
import { tmc } from '@/core/realisations/tmc.realisation'

// composable
import useAccount from '@/common/composable/useAccount'
import { useRound } from '@/common/composable/useRound'
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        TheButton,
        TableScrollWrapper,
        TheTable,
        Cheap,
        TmcAdmissionsFilters,
    },
    setup() {
        const { havePermissions } = useAccount()
        const { roundTo } = useRound()
        const { saveFile } = useFile()
        const notifications = useNotifications()

        const state = reactive({
            applications: new DataState<AdmissionT>({}),
        })

        const filters_state = reactive({
            id: null as string | null,
            companyId: null as number | null,
            code1C: null as string | null,
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
            inventoryItemId: null as number | null,
            statusId: null as number | null,
            jiraNumber: null as string | null,
            warrantNumber: null as string | null,
            isSecondMaterial: null as number | null,
            //
            limit: 50,
        })

        const loadApplications = async (page = 1) => {
            state.applications.pagination.current_page = page

            try {
                state.applications.is_loading = true

                const response = await tmc.getAdmissionsList({
                    page,
                    ...filters_state,
                })

                state.applications.list = response.data
                state.applications.pagination.total_pages = response.meta.last_page
                state.applications.pagination.total_items = response.meta.total

                state.applications.is_failed = false
                state.applications.failure.error_state.is_cancel = false
            } catch (error) {
                state.applications.failure.catchError(error)

                if (!state.applications.failure.error_state.is_cancel) {
                    state.applications.is_failed = true
                }
            } finally {
                if (!state.applications.failure.error_state.is_cancel) {
                    state.applications.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadApplications(state.applications.pagination.current_page)
        }

        onBeforeMount(loadApplications)

        const onSubmitFilters = (filters: TmcGetAdmissionsListParams) => {
            filters_state.id = filters.id
            filters_state.companyId = filters.companyId
            filters_state.code1C = filters.code1C
            filters_state.createdAtFrom = filters.createdAtFrom
            filters_state.createdAtTo = filters.createdAtTo
            filters_state.inventoryItemId = filters.inventoryItemId
            filters_state.jiraNumber = filters.jiraNumber
            filters_state.statusId = filters.statusId
            filters_state.warrantNumber = filters.warrantNumber
            filters_state.isSecondMaterial = filters.isSecondMaterial

            loadApplications(1)
        }

        const onResetFilters = () => {
            filters_state.id = null
            filters_state.companyId = null
            filters_state.code1C = null
            filters_state.createdAtFrom = null
            filters_state.createdAtTo = null
            filters_state.inventoryItemId = null
            filters_state.jiraNumber = null
            filters_state.statusId = null
            filters_state.warrantNumber = null
            filters_state.isSecondMaterial = null

            loadApplications(1)
        }

        const cutName = (name: string, max_length: number): string => {
            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const exportExcel = async () => {
            try {
                state.applications.is_loading = true
                const response = await tmc.exportExcelAdmissions(filters_state)
                saveFile(response, 'Поступление')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.applications.is_loading = false
            }
        }

        const isCanEdit = (row: AdmissionT) => {
            return (
                havePermissions(['receipts-store', 'all-root-permission']) &&
                (row.statusId === 1 || (row.isSecondMaterial && row.statusId !== 3))
            )
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            state,
            filters_state,
            //
            loadApplications,
            havePermissions,
            onSubmitFilters,
            onResetFilters,
            exportExcel,
            onLimitChange,
            //
            roundTo,
            cutName,
            isCanEdit,
            formatDate,
            //
            TmcAdmissionsColumns,
        }
    },
})
